import { Injectable, signal, computed, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { CandidateProfile, Gender, jeKorisnikFirme, UserProfile, UserRole } from '../models/user-profile.model';
import { AuthenticationResponseDto } from '../models/auth.model';
import { mapAuthResponseToProfile, mapBackendRole, mapCandidateProfileDtoToProfile, mapDomainToUpdateRequest } from '../mappers/auth.mapper';
import { AuthApiService } from './auth-api.service';
import { CandidatePortalApiService } from './candidate-portal-api.service';
import { InviteApiService } from './invite-api.service';
import { AcceptInviteRequestDto } from '../models/invite.model';
import { roleHomePath } from '../../../shared/utils/role-home.util';
import { sigurnaPovratnaPutanja } from '../../../shared/utils/povratak.util';

const STORAGE_KEY = 'tm_candidate_profile';
const TOKEN_KEY = 'tm_access_token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly authApi = inject(AuthApiService);
  private readonly portalApi = inject(CandidatePortalApiService);
  private readonly inviteApi = inject(InviteApiService);
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly user = signal<UserProfile | null>(null);
  private readonly token = signal<string | null>(null);
  private refreshInProgress$: Observable<string> | null = null;

  readonly isLoggedIn = computed(() => !!this.user());
  readonly currentUser = this.user.asReadonly();
  readonly accessToken = this.token.asReadonly();

  readonly initials = computed(() => {
    const u = this.user();
    if (!u?.name) return '';
    return u.name
      .split(' ')
      .map(w => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  });

  readonly companyId = computed<number | null>(() => {
    const u = this.user();
    return u && jeKorisnikFirme(u) ? (u.companyId ?? null) : null;
  });

  readonly companyName = computed<string | null>(() => {
    const u = this.user();
    return u && jeKorisnikFirme(u) ? u.companyName : null;
  });

  readonly gender = computed<Gender>(() => this.user()?.gender ?? 'male');
  readonly role = computed<UserRole>(() => this.user()?.role ?? 'candidate');

  /**
   * Kamo nakon uspješne prijave, ako ne na home role: `?returnUrl=` s /login (postavlja ga
   * stranica prijave, već provjeren kroz `sigurnaPovratnaPutanja`). Troši se jednom.
   */
  private povratak: string | null = null;

  postaviPovratak(url: string | null): void {
    this.povratak = sigurnaPovratnaPutanja(url);
  }

  private redirectByRole(role: UserRole): void {
    const povratak = role === 'candidate' ? null : this.povratak;
    this.povratak = null;
    if (povratak) {
      this.router.navigateByUrl(povratak);
    } else {
      this.router.navigate(roleHomePath(role));
    }
  }

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try { this.user.set(JSON.parse(stored)); } catch { /* ignore corrupt data */ }
      }
      this.token.set(localStorage.getItem(TOKEN_KEY));
    }
  }

  /**
   * Email login. Vraća COLD Observable — caller MORA subscribe-ati
   * (inače se request ne pošalje). Na uspjeh: token + profil + redirect.
   */
  loginWithEmail(email: string, password: string): Observable<void> {
    return this.authApi.authenticate({ email, password }).pipe(
      tap(dto => this.handleAuthSuccess(dto, email)),
      switchMap(dto => this.loadFullProfile(mapBackendRole(dto.role))),
      map(() => undefined),
    );
  }

  /**
   * Google login. Vraća COLD Observable — caller MORA subscribe-ati.
   * Email mora proslijediti caller jer backend response ga ne vraća
   * (caller ga ima iz SocialUser-a kojeg lib dekodira iz idToken-a).
   */
  loginWithGoogle(idToken: string, email: string): Observable<void> {
    return this.authApi.authenticateGoogle(idToken).pipe(
      tap(dto => this.handleAuthSuccess(dto, email)),
      switchMap(dto => this.loadFullProfile(mapBackendRole(dto.role))),
      map(() => undefined),
    );
  }

  /**
   * Facebook login. Vraća COLD Observable — caller MORA subscribe-ati.
   * Email i accessToken caller dobije iz SocialUser-a kojeg lib emitira
   * iz `socialAuthService.authState`.
   */
  loginWithFacebook(accessToken: string, email: string): Observable<void> {
    return this.authApi.authenticateFacebook(accessToken).pipe(
      tap(dto => this.handleAuthSuccess(dto, email)),
      switchMap(dto => this.loadFullProfile(mapBackendRole(dto.role))),
      map(() => undefined),
    );
  }

  /**
   * Prihvati company invite — kreira račun u firmi i odmah logira (backend
   * vraća isti AuthenticationResponse kao login). Vraća COLD Observable —
   * caller MORA subscribe-ati. `email` dolazi iz invite metadate jer ga auth
   * response ne vraća. handleAuthSuccess postavi token + profil i preusmjeri
   * po roli (recruiter → /dashboard). Bez loadFullProfile — pozvani korisnik
   * je staff rola, ne candidate.
   */
  acceptInvite(token: string, body: AcceptInviteRequestDto, email: string): Observable<void> {
    return this.inviteApi.accept(token, body).pipe(
      tap(dto => this.handleAuthSuccess(dto, email)),
      map(() => undefined),
    );
  }

  /**
   * Update candidate profila. Vraća COLD Observable — caller MORA subscribe-ati.
   * Local merge konstruira request DTO; što sleće u state je backend response
   * (source of truth) preko mappera koji re-derivira gender i preserve-a role.
   *
   * CV polja (hasCv/cvFileName/cvUploadedAt) NISU u ovom formu — vlasništvo su
   * zasebnih /my-profile/cv endpointa. Preserve-amo ih iz `merged` (= trenutni
   * state) da Spremi promjene nikad ne obriše CV, neovisno o tome vraća li ih PUT.
   */
  updateProfile(partial: Partial<CandidateProfile>): Observable<CandidateProfile> {
    const current = this.user();
    if (!current || current.role !== 'candidate') return throwError(() => new Error('Not a candidate'));

    const merged: CandidateProfile = { ...current, ...partial };
    const dto = mapDomainToUpdateRequest(merged);

    return this.portalApi.updateProfile(dto).pipe(
      map(response => ({
        ...mapCandidateProfileDtoToProfile(response, 'candidate'),
        hasCv: merged.hasCv ?? false,
        cvFileName: merged.cvFileName ?? null,
        cvUploadedAt: merged.cvUploadedAt ?? null,
      })),
      tap(profile => this.setUser(profile)),
    );
  }

  /**
   * Re-fetch /my-profile i resync user signala. Public — koristi se nakon
   * CV upload/delete da pokupi kanonski cvFileName/cvUploadedAt (upload
   * response ih ne vraća). Swallow-a grešku kao loadFullProfile (poziva se tek
   * nakon već uspješne CV operacije → optimistic state ostaje ako GET padne).
   */
  reloadProfile(): Observable<unknown> {
    const current = this.user();
    if (!current || current.role !== 'candidate') return of(undefined);
    return this.portalApi.getMyProfile().pipe(
      tap(dto => {
        const cur = this.user();
        if (!cur || cur.role !== 'candidate') return;
        this.setUser(mapCandidateProfileDtoToProfile(dto, 'candidate'));
      }),
      catchError(() => of(undefined)),
    );
  }

  logout(): void {
    // Backend obriše refresh cookie (docs/sigurnost-prijave.md §6.2). Greška se ignorira —
    // odjava na ovom uređaju mora proći i bez mreže.
    this.authApi.logout().subscribe({ error: () => undefined });
    this.zaboraviPrijavu();
    this.router.navigate(['/']);
  }

  /**
   * Prijava na ovom uređaju prestaje bez poziva backendu i bez preusmjeravanja. K5: nakon brisanja računa
   * (backend je već poništio tokene i obrisao refresh cookie) stranica još pokazuje „Podaci su obrisani”.
   */
  zaboraviPrijavu(): void {
    this.user.set(null);
    this.token.set(null);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
    }
    // Čisti cache GIS lib-a — bez ovog bi se SocialUser cached state
    // mogao re-emitirati pri povratku na /login i automatski relogin-ati.
    this.socialAuthService.signOut().catch(() => { /* ignore: lib baca ako nije signed-in */ });
  }

  refreshAccessToken(): Observable<string> {
    if (this.refreshInProgress$) return this.refreshInProgress$;

    this.refreshInProgress$ = this.authApi.refreshToken().pipe(
      tap(dto => {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem(TOKEN_KEY, dto.token);
        }
        this.token.set(dto.token);
      }),
      map(dto => dto.token),
      shareReplay(1),
      finalize(() => { this.refreshInProgress$ = null; }),
    );
    return this.refreshInProgress$;
  }

  /**
   * Registracija izvođača (I1) — odgovor backenda je isti kao kod prijave. Nakon spremanja
   * tokena ide na onboarding (`odrediste`, npr. /dashboard/dobrodoslica), ne na home role.
   * Isti email preko Googlea/Facebooka backend spaja u postojeći račun i tada vraća njegove podatke.
   */
  prijavaNakonRegistracije(dto: AuthenticationResponseDto, email: string, odrediste: string): void {
    this.spremiPrijavu(dto, email);
    this.router.navigateByUrl(odrediste);
  }

  /** Novi tokeni bez preusmjeravanja (R4 „Promijeni email”: JWT nosi email, pa se mijenja i token). */
  zamijeniPrijavu(dto: AuthenticationResponseDto, email: string): void {
    this.spremiPrijavu(dto, email);
  }

  /** Spremi token, izgradi profil iz auth response-a, postavi usera i preusmjeri po roli. */
  private handleAuthSuccess(dto: AuthenticationResponseDto, email = ''): void {
    const profile = this.spremiPrijavu(dto, email);
    this.redirectByRole(profile.role);
  }

  private spremiPrijavu(dto: AuthenticationResponseDto, email: string): UserProfile {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(TOKEN_KEY, dto.token);
    }
    this.token.set(dto.token);
    const profile = mapAuthResponseToProfile(dto, email);
    this.setUser(profile);
    return profile;
  }

  /**
   * Chained nakon authenticate-a u login flow-ovima. Rehidrira profil iz
   * baze samo za candidate role. GET failure swallow-a → login se i dalje
   * uspješno završi s minimal profile-om (NE blokira login).
   */
  private loadFullProfile(role: UserRole): Observable<unknown> {
    if (role !== 'candidate') return of(undefined);
    return this.portalApi.getMyProfile().pipe(
      tap(dto => {
        const current = this.user();
        if (!current || current.role !== 'candidate') return;
        const profile = mapCandidateProfileDtoToProfile(dto, 'candidate');
        this.setUser(profile);
      }),
      catchError(() => of(undefined)),
    );
  }

  private setUser(profile: UserProfile): void {
    this.user.set(profile);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    }
  }
}
